import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SpinnerCircular } from 'spinners-react';

import DeviceTimezoneApi, { DeviceTimezone } from '../apis/DeviceTimezoneApi';
import { getActiveTimezoneOptions, findTimezoneOption } from '../utils/timezones';
import Message from '../utils/Message';

interface AccountDeviceTimezonesProps {
  accountId: string;
}

// Default far-future expiration so the manual override is not auto-overwritten
// by the device's AccuWeather re-sync.
const DEFAULT_NEXT_OFFSET_DATE = '2099-01-01';

const formatOffset = (offset: number | null): string => {
  if (offset == null) return ' - ';
  return offset > 0 ? `+${offset}` : `${offset}`;
};

const formatDate = (value: string | null): string => {
  if (!value) return ' - ';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const AccountDeviceTimezones: React.FC<AccountDeviceTimezonesProps> = ({ accountId }) => {
  const [devices, setDevices] = useState<DeviceTimezone[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selected, setSelected] = useState<DeviceTimezone | null>(null);
  const [nextOffsetDate, setNextOffsetDate] = useState<string>(DEFAULT_NEXT_OFFSET_DATE);

  const selectRef = useRef<HTMLSelectElement>(null);

  const timezoneOptions = useMemo(() => getActiveTimezoneOptions(), []);

  const loadDevices = () => {
    setLoading(true);
    DeviceTimezoneApi.getAccountDeviceTimezones(accountId).then((rsp) => {
      setLoading(false);
      if (rsp.ok && Array.isArray(rsp.data)) {
        const sorted = [...rsp.data].sort((a, b) => (a.DeviceName || '').toLowerCase().localeCompare((b.DeviceName || '').toLowerCase()));
        setDevices(sorted);
      } else {
        Message.show('Error getting account device timezones, please try again.');
      }
    });
  };

  useEffect(() => {
    window.$('.modal').modal({ dismissible: false });
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const handleEditClicked = (device: DeviceTimezone) => {
    setSelected(device);
    setNextOffsetDate(DEFAULT_NEXT_OFFSET_DATE);
    window.$('#modalDeviceTimezone').modal('open');
    // Pre-select the current offset in the dropdown when it matches an option.
    setTimeout(() => {
      if (selectRef.current && device.GmtOffset != null) {
        const value = device.GmtOffset > 0 ? `+${device.GmtOffset}` : `${device.GmtOffset}`;
        const hasOption = timezoneOptions.some((tz) => tz.valueString === value);
        if (hasOption) {
          selectRef.current.value = value;
        }
      }
    }, 0);
  };

  const handleCancel = () => {
    window.$('#modalDeviceTimezone').modal('close');
    setSelected(null);
  };

  const handleConfirm = () => {
    if (!selected) return;
    const value = selectRef.current?.value;
    if (!value) {
      Message.show('Please select the new timezone');
      return;
    }
    const option = findTimezoneOption(value);
    if (!option) {
      Message.show('Please select a valid timezone');
      return;
    }
    if (!nextOffsetDate) {
      Message.show('Please select an expiration date');
      return;
    }

    setSubmitting(true);
    DeviceTimezoneApi.overrideDeviceTimezone(selected.DeviceID, {
      GmtOffset: parseInt(option.valueString, 10),
      Code: option.shortName,
      Name: option.name,
      NextOffsetChange: `${nextOffsetDate}T00:00:00.000Z`,
    }).then((rsp) => {
      setSubmitting(false);
      if (rsp.ok) {
        if (rsp.cacheReset === false) {
          Message.show('Timezone saved, but the device info cache could not be refreshed. It may take longer to apply.');
        } else {
          Message.show('Device timezone updated!');
        }
        handleCancel();
        loadDevices();
      } else {
        Message.show('Error updating device timezone, please try again');
      }
    });
  };

  const renderRows = () => {
    if (!devices.length) {
      return (
        <tbody className="center-align">
          <tr style={{ borderBottomStyle: 'none' }}>
            <td colSpan={5} className="center-align">
              <br />
              <br />
              <span className="nucleus-table-header-medium text-gray">There are no devices for this account</span>
              <br />
              <br />
            </td>
          </tr>
        </tbody>
      );
    }
    return (
      <tbody className="tbody-ss">
        {devices.map((device) => (
          <tr key={device.DeviceID}>
            <td className="table-col-30 left-align">{device.DeviceName || ' - '}</td>
            <td className="table-col-15 center-align">{formatOffset(device.GmtOffset)}</td>
            <td className="table-col-25 center-align">{device.Code || ' - '}</td>
            <td className="table-col-20 center-align">{formatDate(device.CacheLastRefreshDate)}</td>
            <td className="table-col-10 nucleus-row center-align">
              <span className="nucleus-table-icon nucleus-link" onClick={() => handleEditClicked(device)}>
                <i className="Tiny material-icons">edit</i>
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    );
  };

  return (
    <div>
      <table className="bordered highlight nucleus-table">
        <thead className="thead-ss">
          <tr>
            <th className="table-col-30 left-align">
              <span className="nucleus-table-header-medium">Device Name</span>
            </th>
            <th className="table-col-15 center-align">
              <span className="nucleus-table-header-medium">GMT Offset</span>
            </th>
            <th className="table-col-25 center-align">
              <span className="nucleus-table-header-medium">Timezone</span>
            </th>
            <th className="table-col-20 center-align">
              <span className="nucleus-table-header-medium">Last Refresh</span>
            </th>
            <th className="table-col-10 center-align">
              <span className="nucleus-table-header-medium">Edit</span>
            </th>
          </tr>
        </thead>
        {!loading && renderRows()}
      </table>

      {loading && (
        <div className="row center-align">
          <br />
          <br />
          <SpinnerCircular color="#2096F3" secondaryColor="rgba(0,0,0,0.16)" size={50} thickness={100} />
          <br />
          <br />
        </div>
      )}

      <div id="modalDeviceTimezone" className="modal modalDeviceTimezone">
        <div className="modal-content nucleus-modal-wrapper-scrollable2">
          <img className="responsive-img img-close-modal" src="img/close_modal.png" onClick={handleCancel} alt="Close" />
          <h3 className="nucleus-page-subtitle">Set Device Timezone</h3>
          <br />
          <div className="row">
            <div className="col s12 no-margin">
              <span className="nucleus-labels">You are overriding the timezone of {selected?.DeviceName || ' - '}</span>
            </div>
            <div className="col s12 no-margin">
              <span className="nucleus-labels">Current GMT Offset: &nbsp;{formatOffset(selected?.GmtOffset ?? null)}</span>
            </div>
            <div className="col s12 no-margin">
              <span className="nucleus-labels">Current Timezone: &nbsp;{selected?.Code || ' - '}</span>
            </div>
            <div className="col s12 no-margin">
              <span className="nucleus-labels">Last Cache Refresh: &nbsp;{formatDate(selected?.CacheLastRefreshDate ?? null)}</span>
            </div>
          </div>

          <div className="row">
            <div className="col s12 no-margin">
              <span className="nucleus-labels">Select the new timezone:</span>
              <br />
              <select ref={selectRef} className="browser-default nucleus-select select-large">
                {timezoneOptions.map((tz) => (
                  <option key={tz.valueString + tz.shortName} value={tz.valueString}>
                    {tz.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col s12 no-margin">
              <br />
              <span className="nucleus-labels">Override holds until (expiration date):</span>
              <br />
              <input type="date" value={nextOffsetDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setNextOffsetDate(e.target.value)} className="nucleus-input-form" />
            </div>

            <div className="input-field col s12 no-margin">
              <br />
              <div className="row center-align">
                <div className="col s6 center-align no-margin">
                  <a className={'txt-white nucleus-font-medium ' + (submitting ? 'nucleus-submit-btn-disabled' : 'nucleus-submit-btn')} onClick={submitting ? undefined : handleCancel}>
                    Cancel
                  </a>
                </div>
                <div className="col s6 center-align no-margin">
                  <a className={'txt-white nucleus-font-medium ' + (submitting ? 'nucleus-submit-btn-disabled' : 'nucleus-submit-btn')} onClick={submitting ? undefined : handleConfirm}>
                    {submitting ? 'Updating...' : 'Confirm'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDeviceTimezones;
